import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import { useSearchParams } from "react-router-dom";
import { journalsQueries, type components } from "@/entities/journal";
import { useCoordinator, useLocalUser } from "@/shared/lib/hooks";
import "./journal-banner-section-two.scss";

type SectionTwoListData = {
  total?: number;
  section2List?: {
    status?: string;
    signer?: { userId?: string };
  }[];
};

type ResponsiblePerson =
  components["schemas"]["UserHistoryWithRolesAndCompanyDto"];

const isCurrentUserInRole = (
  people: ResponsiblePerson[] | undefined,
  userId?: string,
) =>
  Boolean(
    userId &&
      people?.some(
        (person) =>
          person.userHistory?.userId === userId &&
          person.userHistory?.roleInfo?.isCurrently !== false,
      ),
  );

export const JournalBannerSectionTwo = () => {
  const { mainJournalId } = useCoordinator();
  const localUser = useLocalUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: mainJournal } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );
  const sectionTwoId = mainJournal?.mainJournal?.section2?.sectionId || "";

  const { data: responsible } = useQuery({
    ...journalsQueries.getResponsibleSectionTwoList(sectionTwoId),
    enabled: !!sectionTwoId,
  });

  const { data: records } = useQuery({
    ...journalsQueries.getSection2({
      sectionTwoId,
      page: 1,
      limit: 1000,
    }),
    enabled: !!sectionTwoId,
  });

  const isInputter = useMemo(
    () => isCurrentUserInRole(responsible?.inputters, localUser?.user_id),
    [responsible?.inputters, localUser?.user_id],
  );

  const awaitingCount = (records as SectionTwoListData | undefined)?.section2List?.filter(
    (item) =>
      item.status === "AWAITING_SIGNATURE" &&
      item.signer?.userId === localUser?.user_id,
  ).length ?? 0;
  const rejectedCount = (records as SectionTwoListData | undefined)?.section2List?.filter(
    (item) => item.status === "REJECTED",
  ).length ?? 0;
  const showSignBanner = awaitingCount > 0;
  const showRejectBanner = isInputter && rejectedCount > 0;

  const applyStatus = (status: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("status", status);
    setSearchParams(params);
  };

  if (!showSignBanner && !showRejectBanner) {
    return null;
  }

  return (
    <div className="journal-banner-section-two">
      {showSignBanner ? (
        <div className="journal-banner">
          <div>
            Просим подписать записи раздела. Количество записей на подпись -{" "}
            {awaitingCount}
          </div>
          <Button
            className="journal-banner-section-two__button"
            onClick={() => applyStatus("AWAITING_SIGNATURE")}
          >
            Посмотреть
          </Button>
        </div>
      ) : null}
      {showRejectBanner ? (
        <div className="journal-banner">
          <div>
            Просим внести изменения в отклонённые записи раздела. Количество
            записей отклонено - {rejectedCount}
          </div>
          <Button
            className="journal-banner-section-two__button"
            onClick={() => applyStatus("REJECTED")}
          >
            Посмотреть
          </Button>
        </div>
      ) : null}
    </div>
  );
};
